/**
 * Merchant back office (merchant domain, `/admin`).
 *
 * REAL: catalog, inventory, settings, users and roles go through the shared
 * `crud` door (ProductModel, InventoryUnit, Listing, Category, Setting, User, Role),
 * plus the model actions below. Every merchant screen is real since D7; no fixtures remain.
 */

const crudAction = (model, action, data) => ({ url: `/api/crud/${model}`, method: 'POST', data: { action, data } });

export default {
  // SELL-01 dashboard (sales + orders KPIs, trend, status mix, recent orders)
  merchantSalesSummary: () => crudAction('SalesOrder', 'summary', {}),
  // MERCH-58: "Get your store ready" (store:view) and stock value at listing prices (inventory:view)
  merchantSetupChecklist: () => crudAction('Location', 'checklist', {}),
  merchantStockValue: () => crudAction('InventoryUnit', 'stockValue', {}),

  // ---- real (D1) ----------------------------------------------------------
  // SELL-03 inspection checklist (PRD S-03): queue = completed + pending/overdue units
  merchantInspections: (params = {}) => crudAction('InspectionReport', 'queue', params),
  inspectionChecklist: ({ unit_id }) => crudAction('InspectionReport', 'checklist', { unit_id }),
  storeProductTypes: () => crudAction('Category', 'productTypes', {}),
  setStoreProductTypes: ({ types }) => crudAction('Category', 'setProductTypes', { types }),
  // The store's own fields per product type (ADR 0009 §4, MERCH-86): { types, kinds, max_fields }; save replaces one type's list.
  storeCustomAttributes: () => crudAction('Category', 'customAttributes', {}),
  setStoreCustomAttributes: ({ product_type, fields }) => crudAction('Category', 'setCustomAttributes', { product_type, fields }),
  // MERCH-87: a listing's options and variants (catalog:view / catalog:update; saves audited, all-or-nothing).
  listingVariants: ({ listing_id }) => crudAction('ListingVariant', 'matrix', { listing_id }),
  saveListingVariants: ({ listing_id, options, variants, remove }) => crudAction('ListingVariant', 'save', { listing_id, options, variants, remove }),
  // Store features (D101): [{ key, label, description, enabled, is_default }] (store:view); switch with store:features, audited.
  storeFeatures: () => crudAction('Location', 'features', {}),
  // confirm: switch repairs off despite open tickets/unpaid invoices (a 409 FEATURE_IN_USE lists them).
  setStoreFeatures: ({ features, confirm }) => crudAction('Location', 'setFeatures', confirm ? { features, confirm: true } : { features }),
  // Store email (MERCH-107, D117): the store's own SMTP, store:email (Owner). The password is never returned.
  storeEmailSettings: () => crudAction('Location', 'email', {}),
  saveStoreEmailSettings: (data) => crudAction('Location', 'saveEmail', data),
  testStoreEmail: ({ to }) => crudAction('Location', 'testEmail', { to }),
  // { unit_id, technician, results, battery?: { method, health_pct } } → queue row
  merchantInspectionSave: ({ unit_id, technician, results, battery, accessories }) =>
    crudAction('InspectionReport', 'record', { unit_id, technician, results, battery, accessories }),
  // SELL-02 bulk CSV import: client pre-validates, the server re-validates and imports atomically
  // Appearance (D24–D27): landing page + brand kit as a versioned draft. Needs store:appearance.
  // The editor stores the draft on open (so uploads can attach to it); MCP reads use 'draft'.
  appearanceDraft: () => crudAction('StorefrontPage', 'openDraft', {}),
  // B15: `doc` is the whole store document (home, listing, product, chrome, card); `page` alone still saves the landing page.
  appearanceSave: ({ doc, page, theme, lock_version }) => crudAction('StorefrontPage', 'saveDraft', doc ? { doc, theme, lock_version } : { page, theme, lock_version }),
  appearanceKits: () => crudAction('StorefrontPage', 'kits', {}),
  appearanceApplyKit: ({ kit, lock_version }) => crudAction('StorefrontPage', 'applyKit', { kit, lock_version }),
  appearancePreviewLink: () => crudAction('StorefrontPage', 'previewLink', {}),
  // MERCH-55: pre-publish QA; publish answers 422 QA_WARNINGS unless acknowledge_warnings is true.
  appearanceQa: () => crudAction('StorefrontPage', 'qa', {}),
  appearancePublish: ({ lock_version, acknowledge_warnings } = {}) => crudAction('StorefrontPage', 'publish', { lock_version, ...(acknowledge_warnings ? { acknowledge_warnings: true } : {}) }),
  appearanceHistory: () => crudAction('StorefrontPage', 'history', {}),
  appearanceRollback: ({ version }) => crudAction('StorefrontPage', 'rollback', { version }),
  // Custom HTML home (ADR 0007): needs store:custom_code. Errors come back as details html:<line>:<column>.
  customHomeReference: () => crudAction('StorefrontTemplate', 'reference', {}),
  customHomeDraft: () => crudAction('StorefrontTemplate', 'draft', {}),
  customHomeValidate: ({ html }) => crudAction('StorefrontTemplate', 'validate', { html }),
  customHomeSave: ({ html, lock_version }) => crudAction('StorefrontTemplate', 'saveDraft', { html, lock_version }),
  customHomePreviewLink: () => crudAction('StorefrontTemplate', 'previewLink', {}),
  customHomePublish: ({ lock_version } = {}) => crudAction('StorefrontTemplate', 'publish', { lock_version }),
  customHomeDisable: () => crudAction('StorefrontTemplate', 'disable', {}),
  customHomeRollback: ({ version }) => crudAction('StorefrontTemplate', 'rollback', { version }),
  customHomeHistory: () => crudAction('StorefrontTemplate', 'history', {}),
  // MERCH-54: what a listing still needs before it can be published → { ready, score, missing, can_publish }
  listingReadiness: ({ id }) => crudAction('Listing', 'readiness', { id }),
  // MERCH-58: list screens in one call each → { data: { [id]: { ready, missing, photos, inspection, cover_url | location } } }
  listingOverview: ({ ids }) => crudAction('Listing', 'overview', { ids }),
  unitOverview: ({ ids }) => crudAction('InventoryUnit', 'overview', { ids }),
  // MERCH-59: a unit's photos (approved, pending review, rejected) and the staff review of an assistant's upload.
  unitPhotos: ({ unit_id }) => crudAction('Media', 'photos', { unit_id }),
  reviewUnitPhoto: ({ media_id, decision, reason }) => crudAction('Media', 'reviewPhoto', { media_id, decision, ...(reason ? { reason } : {}) }),
  // A product model's catalog images (MERCH-81, D95).
  modelImages: ({ product_model_id }) => crudAction('ProductModel', 'images', { product_model_id }),
  reviewModelImage: ({ media_id, decision, reason }) => crudAction('ProductModel', 'reviewImage', { media_id, decision, ...(reason ? { reason } : {}) }),
  merchantImportSubmit: ({ rows }) => crudAction('InventoryUnit', 'import', { rows }),


  // SELL-05 orders & fulfilment
  merchantOrders: (params) => crudAction('SalesOrder', 'queue', params || {}),
  merchantOrder: ({ id }) => crudAction('SalesOrder', 'manage', { id }),
  merchantOrderStep: (data) => crudAction('SalesOrder', 'step', data),

  // SELL-06 customers & leads
  // SELL-06 leads (real since D7; contact details consent-scoped by the server)
  merchantLeads: (params) => crudAction('Lead', 'inbox', params || {}),
  merchantLeadAdd: (data) => crudAction('Lead', 'add', data),
  merchantLeadUpdate: (data) => crudAction('Lead', 'stage', data),
  merchantLeadQuote: (data) => crudAction('Lead', 'quote', data),
  // MERCH-58: { id, next_action?, follow_up_at? } → lead; audited lead.follow_up
  merchantLeadFollowUp: (data) => crudAction('Lead', 'followUp', data),

  // SELL-07 settlement center
  merchantSettlements: () => crudAction('SettlementLine', 'summary', {}), // real since D6
  // MERCH-58: reconciliation CSV { state?, type?, from?, to? } → { filename, mime, content, rows }; audited settlement.export
  merchantSettlementExport: (params) => crudAction('SettlementLine', 'export', params || {}),

  // SELL-08 store setup (real since D7)
  // Connected AI assistants (D48): own, or the whole store for core:manage_tokens.
  assistantConnections: ({ all = false } = {}) => crudAction('ApiToken', 'connections', { all }),
  assistantDisconnect: ({ kind, id }) => crudAction('ApiToken', 'disconnect', { kind, id }),
  merchantDomains: () => crudAction('Location', 'domains', {}),
  merchantDomainAdd: (data) => crudAction('Location', 'addDomain', data),
  merchantDomainVerify: (data) => crudAction('Location', 'verifyDomain', data),
  merchantDomainRemove: (data) => crudAction('Location', 'removeDomain', data),
  merchantLocations: () => crudAction('Location', 'setup', {}),
  merchantLocationsSave: (data) => crudAction('Location', 'save', data),
  merchantPolicies: () => crudAction('StorePolicy', 'terms', {}),
  merchantPolicyAccept: (data) => crudAction('StorePolicy', 'accept', data),
  merchantStaffInvites: () => crudAction('StaffInvite', 'invites', {}),
  merchantStaffInvite: (data) => crudAction('StaffInvite', 'invite', data), // → join_url, shown once
  merchantStaffInviteRevoke: (data) => crudAction('StaffInvite', 'revoke', data),
  // Public join page (no session): preview + accept a one-time invite (D14)
  staffInvitePreview: ({ token }) => crudAction('StaffInvite', 'preview', { token }),
  staffInviteJoin: (data) => crudAction('StaffInvite', 'join', data),
};
